import {
  SET_GATEWAYS,
  GATEWAY_UPDATE,
  UPDATE_GATEWAY_FROM_STREAM,
  UPDATE_GATEWAY_CONTROLLERS,
  SET_GATEWAY_CONTROLLERS,
  RESET_GATEWAYS,
  UPDATE_LAST_GATEWAY_STATUS_FROM_STREAM,
  ADD_GATEWAY,
  RECONCILE_GATEWAYS,
} from "../actions/index";
import { reconcileById } from "./reconcile";
import utils from "utils";

const initialData = { byId: {}, allIds: [] };

const setGateways = (_state, payload) => payload;

// `currentDateTime` is a client-side timestamp re-stamped on every snapshot
// fetch, so it must not count as a real change during reconciliation.
const reconcileGateways = (state, payload) =>
  reconcileById(state, payload, { volatileKeys: ["currentDateTime"] });

const resetGateways = () => initialData;

const gatewayUpdate = (state, payload) => {
  if (!payload?.gatewayGuid) return state;
  if (!state.byId?.[payload.gatewayGuid]) return state;
  return {
    ...state,
    byId: {
      ...state.byId,
      [payload.gatewayGuid]: {
        ...state.byId[payload.gatewayGuid],
        name: payload.name,
        telemetryPushInterval: payload.telemetryPushInterval,
        xigVisibility: payload.xigVisibility,
        disableAutomaticDetection: payload.disableAutomaticDetection,
      },
    },
  };
};

const updateLastGatewayStatusFromStream = (state, payload) => {
  const currentGwState = state.byId?.[payload?.gatewayGuid];
  if (!currentGwState) return state;

  // Reference-preserving: a connectivity update that does not actually change
  // the status object keeps the same state/gateway reference so subscribers
  // (e.g. the device single view) do not re-render for nothing.
  if (
    JSON.stringify(currentGwState.lastStatus) === JSON.stringify(payload.status)
  ) {
    return state;
  }

  return {
    ...state,
    byId: {
      ...state.byId,
      [payload.gatewayGuid]: {
        ...currentGwState,
        lastStatus: payload.status,
      },
    },
  };
};

const updateGatewayFromStream = (state, payload) => {
  const currentGwState = state.byId?.[payload?.gatewayGuid];
  if (!currentGwState) return state;

  const isValidDad = payload.disableAutomaticDetection !== undefined && payload.disableAutomaticDetection !== null;

  const merged = {
    status: "status" in payload && payload.status != null
      ? payload.status === 1
        ? "ON"
        : "OFF"
      : currentGwState.status,
    currentDateTime:
      payload.currentDateTime ?? currentGwState.currentDateTime,
    lastDataSentOn: payload.lastSeenOn ?? currentGwState.lastDataSentOn,
    xigVisibility: payload.xigVisibility ?? currentGwState.xigVisibility,
    disableAutomaticDetection: isValidDad
      ? payload.disableAutomaticDetection
      : currentGwState.disableAutomaticDetection,
    interframeDelay: payload.interframeDelay === undefined
        ? currentGwState.interframeDelay
        : payload.interframeDelay,
    releaseFW: payload.releaseFW ?? currentGwState.releaseFW,
    realTimeMode: payload.realTimeMode ?? currentGwState.realTimeMode ?? -1,
  };

  // Reference-preserving: only allocate a new gateway/state when a tracked
  // field actually changed. A redundant or chart-irrelevant twin update then
  // becomes a true no-op and triggers no re-render.
  const changed = Object.keys(merged).some(
    (key) => merged[key] !== currentGwState[key]
  );
  if (!changed) return state;

  return {
    ...state,
    byId: {
      ...state.byId,
      [payload.gatewayGuid]: {
        ...currentGwState,
        ...merged,
      },
    },
  };
};


const updateGatewayControllers = (state, payload) => {
  if (!payload?.gatewayGuid || !payload?.deviceGuid) return state;
  const gw = state.byId?.[payload.gatewayGuid];
  if (!gw || !Array.isArray(gw.devices)) return state;
  const devices = gw.devices.map((d) =>
    d.deviceGuid === payload.deviceGuid
      ? { ...d, friendlyName: payload.friendlyName }
      : d
  );
  return {
    ...state,
    byId: {
      ...state.byId,
      [payload.gatewayGuid]: {
        ...gw,
        devices,
      },
    },
  };
};

const setGatewayControllers = (state, payload) => {
  if (!payload?.gatewayGuid || !Array.isArray(payload?.controllers)) {
    utils.debug(
      "warn",
      `SET_GATEWAY_CONTROLLERS received invalid payload: ${JSON.stringify(payload)}`
    );
    return state;
  }
  const gwId = payload.gatewayGuid;
  const prevGateway = state.byId[gwId] || {};
  const nextGateway = { ...prevGateway, devices: payload.controllers };
  const allIds = state.allIds.includes(gwId)
    ? state.allIds
    : [...state.allIds, gwId];
  return {
    ...state,
    byId: { ...state.byId, [gwId]: nextGateway },
    allIds,
  };
};

const addGateway = (state, payload) => {
  if (!payload?.gatewayGuid) return state;
  return {
    ...state,
    byId: { ...state.byId, [payload.gatewayGuid]: payload },
    allIds: state.allIds.includes(payload.gatewayGuid)
      ? state.allIds
      : [...state.allIds, payload.gatewayGuid],
  };
};

const handlers = {
  [SET_GATEWAYS]: setGateways,
  [RECONCILE_GATEWAYS]: reconcileGateways,
  [RESET_GATEWAYS]: resetGateways,
  [GATEWAY_UPDATE]: gatewayUpdate,
  [UPDATE_LAST_GATEWAY_STATUS_FROM_STREAM]: updateLastGatewayStatusFromStream,
  [UPDATE_GATEWAY_FROM_STREAM]: updateGatewayFromStream,
  [UPDATE_GATEWAY_CONTROLLERS]: updateGatewayControllers,
  [SET_GATEWAY_CONTROLLERS]: setGatewayControllers,
  [ADD_GATEWAY]: addGateway,
};

const gateways = (state = initialData, action) => {
  const handler = handlers[action.type];
  return handler ? handler(state, action.payload) : state;
};

export default gateways;
